import { createPortal } from 'react-dom';
import { ImageIcon, X } from 'lucide-react';
import { AD_CATEGORIES } from '@/features/campaigns/constants/campaignOptions';
import {
  GALLERY_TABS,
  galleryStatusChangedByText,
  isGalleryVideo,
  type GalleryItem,
  type GalleryMediaEntry,
} from '@/features/gallery/types/gallery';

type GalleryDetailModalProps = {
  item: GalleryItem | null;
  onClose: () => void;
};

function MediaTile({
  entry,
  className,
}: {
  entry: GalleryMediaEntry;
  className?: string;
}) {
  const orientation = String(entry.orientation || 'LANDSCAPE').toUpperCase();
  const media = entry.media;
  const video = isGalleryVideo(media);
  const label = orientation === 'PORTRAIT' ? 'Portrait' : 'Landscape';

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border border-[var(--dash-line)] bg-[var(--dash-soft)] ${className || ''}`}
    >
      {media?.url ? (
        video ? (
          <video
            src={media.url}
            className="h-full w-full object-contain"
            controls
            muted
            loop
            playsInline
          />
        ) : (
          <img
            src={media.url}
            alt={`${label} creative`}
            className="h-full w-full object-contain"
          />
        )
      ) : (
        <div className="flex h-full flex-col items-center justify-center gap-2 text-[var(--dash-muted)]">
          <ImageIcon className="h-7 w-7 opacity-40" />
          <span className="text-xs font-medium">No media</span>
        </div>
      )}
      <span className="absolute bottom-2 left-2 rounded-md bg-black/60 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-white uppercase">
        {label}
      </span>
      {video ? (
        <span className="absolute top-2 left-2 rounded-md bg-black/55 px-2 py-0.5 text-[10px] font-medium text-white">
          Video
        </span>
      ) : null}
    </div>
  );
}

export function GalleryDetailModal({ item, onClose }: GalleryDetailModalProps) {
  if (!item) return null;

  const status = String(item.status || 'PENDING').toUpperCase();
  const statusLabel =
    GALLERY_TABS.find((tab) => tab.key === status)?.label || status;
  const statusChangedBy = galleryStatusChangedByText(item);

  const entries = item.mediaIds || [];
  const portrait = entries.find(
    (entry) => String(entry.orientation || '').toUpperCase() === 'PORTRAIT',
  );
  const landscape = entries.find(
    (entry) => String(entry.orientation || '').toUpperCase() === 'LANDSCAPE',
  );
  const hasMedia = Boolean(portrait || landscape);

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close overlay"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      <div className="relative z-10 flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl border border-[var(--dash-line)] bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-[var(--dash-line)] px-5 py-4">
          <div className="min-w-0 text-left">
            <h2 className="m-0 text-lg font-semibold text-[var(--dash-ink)]">
              Gallery detail
            </h2>
            <p className="mt-1 mb-0 truncate text-sm text-[var(--dash-muted)]">
              {item.contentName || 'Media information and preview'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-full border border-[var(--dash-line)] p-2 text-[var(--dash-muted)] hover:bg-[var(--dash-soft)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-5">
          {hasMedia ? (
            <div className="flex flex-col items-center gap-3 sm:h-[260px] sm:flex-row sm:items-stretch">
              {portrait ? (
                <MediaTile
                  entry={portrait}
                  className="h-[280px] w-[158px] shrink-0 sm:h-full sm:w-auto sm:aspect-[9/16]"
                />
              ) : null}
              {landscape ? (
                <MediaTile
                  entry={landscape}
                  className={
                    portrait
                      ? 'aspect-video w-full sm:h-full sm:aspect-auto sm:min-w-0 sm:flex-1'
                      : 'aspect-video w-full'
                  }
                />
              ) : null}
            </div>
          ) : (
            <div className="flex h-[200px] flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-[var(--dash-line)] bg-[var(--dash-soft)] text-[var(--dash-muted)]">
              <ImageIcon className="h-8 w-8 opacity-40" />
              <span className="text-sm font-medium">No media available</span>
            </div>
          )}

          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-xl bg-[var(--dash-soft)] px-3.5 py-3 text-left">
              <p className="m-0 text-[11px] font-semibold tracking-wide text-[var(--dash-muted)] uppercase">
                Content name
              </p>
              <p className="m-0 mt-1 truncate text-sm font-semibold text-[var(--dash-ink)]">
                {item.contentName || 'N/A'}
              </p>
            </div>
            <div className="rounded-xl bg-[var(--dash-soft)] px-3.5 py-3 text-left">
              <p className="m-0 text-[11px] font-semibold tracking-wide text-[var(--dash-muted)] uppercase">
                Status
              </p>
              <p className="m-0 mt-1 truncate text-sm font-semibold text-[var(--dash-ink)]">
                {statusLabel}
              </p>
              {statusChangedBy ? (
                <p
                  className="m-0 mt-1 truncate text-[11px] text-[var(--dash-muted)]"
                  title={statusChangedBy}
                >
                  {statusChangedBy}
                </p>
              ) : null}
            </div>
            <div className="rounded-xl bg-[var(--dash-soft)] px-3.5 py-3 text-left">
              <p className="m-0 text-[11px] font-semibold tracking-wide text-[var(--dash-muted)] uppercase">
                Content type
              </p>
              <p className="m-0 mt-1 truncate text-sm font-semibold text-[var(--dash-ink)]">
                {AD_CATEGORIES[item.contentType || ''] ||
                  item.contentType ||
                  'General'}
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-end border-t border-[var(--dash-line)] px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-5 py-2.5 text-sm font-semibold text-white"
            style={{
              background:
                'linear-gradient(90deg, #8E58A6 0%, #5A4EA6 25%, #145E8C 50%, #007A78 75%, #33A65B 100%)',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
